import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { catchError, map, Observable, of, Subject, switchMap } from 'rxjs';

import { environment } from '../../../environments/environment';
import { hasLegacyAppBase, legacyAppUrl } from '../utils/legacy-app-url';
import { ApiService } from './api.service';

/** The ticket receiver on the legacy portal (auth.aspx.cs `Page_Load`). */
const PORTAL_AUTH_PATH = '/Auth.aspx';

/** Mints the handoff ticket. `[Authorize]`d: the identity comes from the bearer token. */
const HANDOFF_TICKET_PATH = '/Auth/handoff-ticket';

/**
 * Tells the portal to redeem against the LICENSE api rather than the compliance one.
 *
 * Not a security boundary — a forged value only sends the redeem somewhere the ticket does
 * not exist, which fails closed. It exists because the two apps authenticate against
 * different APIs with different JWT signing keys, so each API can only mint, and therefore
 * only redeem, its own tickets.
 */
const PORTAL_SOURCE = 'license';

/** In-app Change Password route: the dev path AND the production fallback. */
export const CHANGE_PASSWORD_PATH = '/changePassword';

/** In-app fallback for Change Security Questions. There is no dedicated in-app page
 *  for this yet (unlike Change Password) — `/securityQuestions` is a LOGIN step
 *  (verifies existing answers to finish signing in), not a self-service change flow,
 *  so it is the wrong target here. `/dashboard` is the same safe landing
 *  `ChangePasswordComponent.handleExit()` already uses for its own exit seam. */
export const CHANGE_SECURITY_QUESTIONS_FALLBACK_PATH = '/dashboard';

/**
 * Values `auth.aspx.cs` branches on with an exact string compare. Transcribed from the
 * receiver, so this is a wire contract, not speculative design. The API whitelists the same
 * four strings when minting, so a typo here fails the mint rather than reaching the portal.
 * `'Logout'` is deliberately absent: logout must go through {@link LogoutService},
 * which also expires the token server-side — routing it through here would silently
 * skip that.
 */
export type LegacyPortalAction = 'Home' | 'Change Password' | 'Change Security';

/**
 * Hands an authenticated user off to the legacy ASP.NET portal for actions the portal
 * still owns (Home, Change Password, Change Security Questions).
 *
 * ## Why this is no longer a payload
 *
 * This used to base64-encode `{ userid, role, Name, CustomerID, Redirecting }` into
 * `Auth.aspx?data=...`, and the portal minted a Forms-Auth session from whatever that
 * payload said. Unsigned, non-expiring, no nonce: anyone could encode any `userid` and be
 * handed that user's session, and the URL was replayable out of browser history, proxy logs
 * and `Referer`.
 *
 * Signing it here could not have fixed that — any key the browser holds is public — so the
 * identity moved out of the browser entirely. This service now asks the API for an opaque,
 * single-use, 60-second ticket; the API derives who the user is from the bearer token and
 * stores it server-side; the portal redeems the ticket over its own server-side call and
 * mints the session from THAT. The browser carries a reference, never a claim.
 *
 * One behavioural consequence worth knowing: the role in the portal's session is now the
 * user's `Role.Code` resolved server-side — the same value the portal's own `Login.aspx`
 * uses — rather than the license role this app happened to have in local storage.
 *
 * ## Deploy order
 *
 * This must not ship before the portal that accepts `?ticket=`. The portal accepts both
 * `ticket=` and the old `data=` through the migration, so receiver-first is safe and this
 * direction is not: an older receiver bounces `?ticket=` straight to its login page.
 *
 * Environment-gated exactly like {@link LogoutService}: development has no portal
 * session to hand off to, so it stays in-app; production leaves the SPA entirely.
 * Falls back to the in-app route (never a dead click) when the portal base is
 * unusable or the mint fails — both would otherwise land the user on a blank
 * `Auth.aspx`.
 */
@Injectable({ providedIn: 'root' })
export class LegacyPortalHandoffService {
  private readonly api = inject(ApiService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly router = inject(Router);

  /** Every accepted handoff intent, funnelled through ONE `switchMap` — see the constructor. */
  private readonly handoffs = new Subject<{ action: LegacyPortalAction; inAppPath: string }>();

  constructor() {
    // Serialised through `switchMap` so the LATEST click wins: a second click (impatient
    // double-click, or Home while Change Password is still minting) unsubscribes the
    // in-flight mint before it can resolve. Without this, two independent mints race and
    // whichever response lands last assigns `window.location.href`, so the user can be sent
    // to the action they did NOT click last.
    //
    // `switchMap` rather than an in-flight boolean guard deliberately: `ApiService.post`
    // carries no timeout, so a guard that ignores clicks until the mint settles would leave
    // Home / Change Password permanently dead against a hung request — the exact failure this
    // service's contract ("never a dead click") exists to prevent. Same reasoning, and the
    // same operator, as the per-page filter stores (CLAUDE.md, *Filter state persistence*).
    //
    // A superseded mint is aborted client-side, but the API may already have written its
    // ticket row; that ticket is simply never redeemed and expires on its own 60-second TTL.
    // Unredeemed is the acceptable half of this trade — redirecting to the wrong action is not.
    //
    // `catchError` stays inside `mintTicket()` (per-request) ON PURPOSE. Hoisted up here it
    // would complete this subscription on the first failed mint and kill every later click.
    this.handoffs
      .pipe(
        switchMap(({ action, inAppPath }) =>
          this.mintTicket(action).pipe(map(ticket => ({ ticket, inAppPath }))),
        ),
        takeUntilDestroyed(),
      )
      .subscribe(({ ticket, inAppPath }) => {
        if (!ticket) {
          this.router.navigateByUrl(inAppPath);
          return;
        }

        window.location.href =
          `${legacyAppUrl(PORTAL_AUTH_PATH)}?ticket=${encodeURIComponent(ticket)}&src=${PORTAL_SOURCE}`;
      });
  }

  /** Sidebar "Home" click — `fallbackPath` is whichever in-app Home path
   *  (`/home`) the caller already resolved, so dev/fallback lands
   *  exactly where a plain in-app Home click always did. */
  home(fallbackPath: string): void {
    this.handoff('Home', fallbackPath);
  }

  /** Change Password from the header profile menu. */
  changePassword(): void {
    this.handoff('Change Password', CHANGE_PASSWORD_PATH);
  }

  /** Change Security Questions from the header profile menu. */
  changeSecurityQuestions(): void {
    this.handoff('Change Security', CHANGE_SECURITY_QUESTIONS_FALLBACK_PATH);
  }

  /**
   * The one seam every public entry point funnels through: decide whether this click can
   * leave the SPA at all, and if it can, ask for a ticket and hand off.
   *
   * Three synchronous bail-outs run first, each landing in-app rather than on a blank
   * `Auth.aspx` — SSR (no `window` to redirect), development (no portal session exists), and
   * a missing/unusable legacy base. The base is checked BEFORE the mint, not after: a ticket
   * we cannot spend is a ticket that should never have been minted, and each one is a row in
   * the portal's ticket table.
   *
   * Past those, the request is pushed onto {@link handoffs} rather than subscribed here, which
   * makes the mint **asynchronous and latest-wins**: a newer click cancels an older in-flight
   * mint, so exactly one redirect happens and it is the one the user asked for last. The
   * external redirect (`window.location.href`, leaving Angular entirely) and the in-app
   * fallback for a failed mint both live in that single subscription in the constructor.
   *
   * Returns immediately in every case — the caller never learns which branch ran, because
   * every branch ends somewhere valid.
   *
   * @param action The `redirecting` intent the portal will branch on.
   * @param inAppPath Where to land instead when the portal cannot be reached.
   */
  private handoff(action: LegacyPortalAction, inAppPath: string): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (!environment.production) {
      this.router.navigateByUrl(inAppPath);
      return;
    }

    if (!hasLegacyAppBase()) {
      this.router.navigateByUrl(inAppPath);
      return;
    }

    this.handoffs.next({ action, inAppPath });
  }

  /**
   * Asks the API for a handoff ticket.
   *
   * The request body carries the intent and nothing else — no user id, no customer id, no
   * role, no name. The endpoint reads all of those from the bearer token's subject, which is
   * the property that makes the handoff unforgeable; sending them would be both useless and
   * a regression waiting to happen.
   *
   * @param action The `redirecting` intent to mint the ticket for.
   * @returns The opaque ticket, or `null` on any failure — an expired session, a network
   *   error, or an unexpected response shape. Never errors, so the caller always falls back.
   */
  private mintTicket(action: LegacyPortalAction): Observable<string | null> {
    return this.api
      .post<{ ticket?: string }>(`${environment.API_BASE_URL}${HANDOFF_TICKET_PATH}`, { redirecting: action })
      .pipe(
        map(response => response.body?.ticket ?? null),
        catchError(() => of(null)),
      );
  }
}
